import React, { useState } from "react";
import api, { formatApiErrorDetail as fmtErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Send, CheckCircle2, Lock, Undo2, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const MOVES = {
  draft: [{ to: "in_review", label: "Submit for review", icon: Send, cls: "bg-indigo-600 hover:bg-indigo-700" }],
  in_review: [
    { to: "draft", label: "Return to draft", icon: Undo2, variant: "outline" },
    { to: "approved", label: "Approve", icon: CheckCircle2, cls: "bg-emerald-600 hover:bg-emerald-700", manager: true },
  ],
  approved: [
    { to: "in_review", label: "Reopen review", icon: RotateCcw, variant: "outline", manager: true },
    { to: "closed", label: "Close RCA", icon: Lock, cls: "bg-slate-900 hover:bg-slate-800", manager: true },
  ],
  closed: [{ to: "approved", label: "Reopen", icon: RotateCcw, variant: "outline", manager: true }],
};

export default function RcaStatusActions({ rca, isManager, dirty, onChanged }) {
  const [move, setMove] = useState(null);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const moves = (MOVES[rca.status] || []).filter((m) => !m.manager || isManager);

  const submit = async () => {
    setLoading(true);
    try {
      await api.post(`/rcas/${rca.id}/status`, { status: move.to, note });
      toast.success(`${rca.key}: ${move.label.toLowerCase()} done`);
      setMove(null); setNote(""); onChanged();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <>
      <div className="flex gap-2 flex-wrap">
        {moves.map((m) => (
          <Button key={m.to} size="sm" variant={m.variant} className={m.cls} disabled={dirty}
            title={dirty ? "Save your changes first" : ""} onClick={() => setMove(m)} data-testid={`rca-move-${m.to}`}>
            <m.icon className="w-3.5 h-3.5 mr-1.5" />{m.label}
          </Button>
        ))}
      </div>
      {move && (
        <Dialog open onOpenChange={(o) => !o && setMove(null)}>
          <DialogContent data-testid="rca-status-dialog">
            <DialogHeader>
              <DialogTitle className="font-display">{move.label}</DialogTitle>
              <DialogDescription>{rca.key} · {rca.title}</DialogDescription>
            </DialogHeader>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)" data-testid="rca-status-note" />
            <DialogFooter>
              <Button variant="ghost" onClick={() => setMove(null)}>Cancel</Button>
              <Button onClick={submit} disabled={loading} className={move.cls} variant={move.variant} data-testid="rca-status-confirm">{loading ? "Saving…" : move.label}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
